import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Plus, Hash, Lock, Trash2, X, Search } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  CHAT PAGE — internal staff messaging (channels + 1-on-1 DM)
//  Polling every 5s for new messages.
// ═══════════════════════════════════════════════════════════════════

const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  if (dt.toDateString() === today.toDateString()) return dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const ChatPage = ({ API_BASE, currentUser }) => {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [unread, setUnread] = useState({});
  const [showNew, setShowNew] = useState(false);
  const scrollRef = useRef(null);
  const userName = currentUser?.fullName || currentUser?.username || 'User';

  // Load channels (auto-create #general if empty)
  const loadChannels = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/chat/channels`, { params: { user: userName } });
      let list = r.data || [];
      if (list.length === 0) {
        await axios.post(`${API_BASE}/chat/seed`);
        const r2 = await axios.get(`${API_BASE}/chat/channels`, { params: { user: userName } });
        list = r2.data || [];
      }
      setChannels(list);
      if (!activeId && list[0]) setActiveId(list[0]._id);
    } catch (e) { /* silent */ }
  }, [API_BASE, userName, activeId]);

  const loadUnread = useCallback(async () => {
    try { const r = await axios.get(`${API_BASE}/chat/unread`, { params: { user: userName } }); setUnread(r.data || {}); }
    catch {}
  }, [API_BASE, userName]);

  const loadMessages = useCallback(async (chId) => {
    if (!chId) return;
    try {
      const r = await axios.get(`${API_BASE}/chat/channels/${chId}/messages`, { params: { limit: 100 } });
      setMessages(r.data || []);
      // Mark as read
      await axios.post(`${API_BASE}/chat/channels/${chId}/read`, { userName });
      loadUnread();
    } catch {}
  }, [API_BASE, userName, loadUnread]);

  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);
  useEffect(() => {
    const t = setInterval(() => { if (activeId) loadMessages(activeId); loadChannels(); }, 5000);
    return () => clearInterval(t);
  }, [activeId, loadMessages, loadChannels]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    try {
      await axios.post(`${API_BASE}/chat/channels/${activeId}/messages`, {
        text: text.trim(), senderName: userName, senderRole: currentUser?.role,
      });
      setText('');
      loadMessages(activeId);
    } catch (e) { alert('Send failed'); }
  };

  const createChannel = async (name, type) => {
    try {
      await axios.post(`${API_BASE}/chat/channels`, {
        name, type: type || 'public', icon: type === 'private' ? '🔒' : '#',
        createdBy: userName,
        members: type === 'private' ? [userName] : [],
      });
      setShowNew(false);
      loadChannels();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const active = channels.find(c => c._id === activeId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, height: 'calc(100vh - 100px)' }}>
      {/* Channel list */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 14px', background: theme.primary, color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}><MessageSquare size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Staff Chat</h3>
            <button onClick={() => setShowNew(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: 6, borderRadius: 6 }}>
              <Plus size={14} />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {channels.map(c => (
            <div key={c._id} onClick={() => setActiveId(c._id)} style={{
              padding: '10px 14px', cursor: 'pointer',
              borderLeft: `3px solid ${activeId === c._id ? theme.primary : 'transparent'}`,
              background: activeId === c._id ? theme.primary + '12' : 'transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>{c.type === 'private' ? '🔒' : c.type === 'dm' ? '💬' : '#'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: theme.navy }}>{c.name}</div>
                  {c.lastMessagePreview && (
                    <div style={{ fontSize: 11, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.lastMessagePreview}
                    </div>
                  )}
                </div>
                {unread[c._id] > 0 && (
                  <span style={{ background: theme.danger, color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>{unread[c._id]}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Messages pane */}
      <div style={{ ...cardStyle, padding: 0, display: 'flex', flexDirection: 'column' }}>
        {active ? (
          <>
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${theme.borderLight}` }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: theme.navy }}>
                {active.type === 'private' ? '🔒' : '#'} {active.name}
              </div>
              {active.description && <div style={{ fontSize: 12, color: theme.textMuted }}>{active.description}</div>}
            </div>

            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: theme.textMuted, padding: 40, fontSize: 13 }}>
                  No messages yet. Start the conversation 👋
                </div>
              ) : messages.map(m => {
                const mine = m.senderName === userName;
                return (
                  <div key={m._id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                    <div style={{
                      maxWidth: '70%',
                      background: mine ? theme.primary : '#f1f5f9',
                      color: mine ? 'white' : theme.navy,
                      padding: '8px 12px', borderRadius: 12,
                      borderTopLeftRadius: mine ? 12 : 4, borderTopRightRadius: mine ? 4 : 12,
                    }}>
                      {!mine && <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 3, opacity: 0.7 }}>{m.senderName}</div>}
                      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text || (m.deleted ? <em>[deleted]</em> : null)}</div>
                      <div style={{ fontSize: 10, marginTop: 4, opacity: mine ? 0.85 : 0.55, textAlign: 'right' }}>
                        {fmtTime(m.createdAt)}{mine && m.readBy.length > 1 && ' ✓✓'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: 10, borderTop: `1px solid ${theme.borderLight}`, display: 'flex', gap: 6 }}>
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Type a message…"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={send} disabled={!text.trim()} style={{ ...btnStyle, marginTop: 0, padding: '10px 16px' }}>
                <Send size={16} />
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: theme.textMuted }}>
            <MessageSquare size={48} style={{ marginBottom: 12, opacity: 0.5 }} />
            <p>Select a channel to start chatting</p>
          </div>
        )}
      </div>

      {showNew && (
        <NewChannelModal onClose={() => setShowNew(false)} onCreate={createChannel} />
      )}
    </div>
  );
};

const NewChannelModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('public');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', padding: 22, borderRadius: 12, width: '100%', maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>New Channel</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <input placeholder="Channel name" value={name} onChange={e => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))} style={{ ...inputStyle, marginBottom: 10 }} />
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {['public', 'private'].map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              flex: 1, padding: 10, borderRadius: 8,
              border: `1.5px solid ${type === t ? theme.primary : theme.border}`,
              background: type === t ? theme.primary + '15' : 'white',
              color: type === t ? theme.primary : theme.textMuted,
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>{t === 'public' ? '# Public' : '🔒 Private'}</button>
          ))}
        </div>
        <button onClick={() => name.trim() && onCreate(name.trim(), type)} disabled={!name.trim()} style={{ ...btnStyle, width: '100%' }}>
          Create Channel
        </button>
      </div>
    </div>
  );
};

export default ChatPage;
